import { FunctionComponent, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	CLIP_NUDGE_SECONDS,
	CLIP_PRECISION_SECONDS,
	MIN_NARRATION_SECONDS,
	WizardTimelineClip,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { formatSeconds } from "../wizard.utils";

/** What the voice line can be changed to. Mirrors the API's own three fields. */
export interface NarrationWindow {
	narrationOffsetSeconds: number;
	narrationInSeconds: number | null;
	narrationOutSeconds: number | null;
}

interface VoiceTrackProps {
	clip: WizardTimelineClip;
	/** The scene's spoken track, once step 3 has run. */
	narrationUrl?: string | null;
	/** The scene's full render length — the axis both tracks are drawn against. */
	durationSeconds: number;
	disabled?: boolean;
	/**
	 * The narration's `<audio>` element, shared upward so the trim preview can start the
	 * voice in sync with the picture. This component's own play button keeps auditioning
	 * the voice alone.
	 */
	audioRef?: RefObject<HTMLAudioElement | null>;
	onChange: (next: NarrationWindow) => void;
	onCommit: () => void;
}

const snap = (seconds: number) =>
	Math.round(seconds / CLIP_PRECISION_SECONDS) * CLIP_PRECISION_SECONDS;
const round1 = (seconds: number) => Math.round(seconds * 10) / 10;

/** What a clip means when nobody has touched its voice. */
export const windowOf = (clip: WizardTimelineClip): NarrationWindow => ({
	narrationOffsetSeconds: clip.narrationOffsetSeconds ?? 0,
	narrationInSeconds: clip.narrationInSeconds ?? null,
	narrationOutSeconds: clip.narrationOutSeconds ?? null,
});

/**
 * The narration's own line under the picture.
 *
 * Three edits, in the convention an editor expects: drag the body to **position** the voice
 * inside the cut, drag the left edge to **cut the head** off the take, drag the right edge
 * to cut the tail. Trimming the head moves the block's edge to the pointer and takes the
 * same amount off the take, so the words that remain stay where they were against the
 * picture — which is the whole reason for doing it by hand rather than by number.
 *
 * The clamping mirrors the server's invariants, but it is a convenience and not the rule:
 * the API checks each of them again and refuses rather than rounds. What it deliberately
 * does *not* prevent is a cut that drops speech, or a voice slid far enough that its tail
 * falls off the end — both are legitimate, and the export warns about the seconds lost.
 */
export const VoiceTrack: FunctionComponent<VoiceTrackProps> = ({
	clip,
	narrationUrl,
	durationSeconds,
	disabled,
	audioRef: sharedAudioRef,
	onChange,
	onCommit,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`videoWizard.steps.cutExport.${key}`, options ?? {}),
		[t]
	);

	const trackRef = useRef<HTMLDivElement>(null);
	const ownAudioRef = useRef<HTMLAudioElement>(null);
	const audioRef = sharedAudioRef ?? ownAudioRef;
	const [dragging, setDragging] = useState<"body" | "head" | "tail" | null>(null);
	const dragOrigin = useRef<{ pointerSeconds: number; window: NarrationWindow } | null>(null);
	// Read off the file itself: the server never sends the take's length, and without it
	// there is nothing to draw the block against or to clamp the tail to.
	const [takeSeconds, setTakeSeconds] = useState<number | null>(null);

	const window_ = windowOf(clip);
	const keptSeconds = round1(clip.outSeconds - clip.inSeconds);
	const from = window_.narrationInSeconds ?? 0;
	const to = window_.narrationOutSeconds ?? takeSeconds ?? keptSeconds;
	const spokenSeconds = Math.max(MIN_NARRATION_SECONDS, round1(to - from));

	// Both tracks share the scene's own axis, so the voice sits visibly under the picture it
	// belongs to rather than against a second, private scale.
	const startSeconds = clip.inSeconds + window_.narrationOffsetSeconds;
	const leftPercent = (startSeconds / durationSeconds) * 100;
	const widthPercent = (spokenSeconds / durationSeconds) * 100;

	const apply = useCallback(
		(next: Partial<NarrationWindow>) => onChange({ ...windowOf(clip), ...next }),
		[clip, onChange]
	);

	/** Where the pointer is, in the scene's seconds. */
	const secondsAt = useCallback(
		(clientX: number) => {
			const track = trackRef.current;

			if (!track) {
				return 0;
			}

			const rect = track.getBoundingClientRect();

			return ((clientX - rect.left) / rect.width) * durationSeconds;
		},
		[durationSeconds]
	);

	const moveTo = useCallback(
		(handle: "body" | "head" | "tail", pointerSeconds: number) => {
			const origin = dragOrigin.current;

			if (!origin) {
				return;
			}

			const start = origin.window;

			if (handle === "body") {
				// The whole block travels; the take is untouched.
				const delta = pointerSeconds - origin.pointerSeconds;
				const offset = snap(start.narrationOffsetSeconds + delta);

				apply({
					// Never past the end of the cut: a voice that starts after the picture is
					// over is refused by the server, and rightly.
					narrationOffsetSeconds: Math.min(
						Math.max(0, offset),
						round1(keptSeconds - CLIP_PRECISION_SECONDS)
					),
				});

				return;
			}

			// Clip-relative, which is what both bounds are measured in.
			const pointer = snap(pointerSeconds - clip.inSeconds);
			const startFrom = start.narrationInSeconds ?? 0;
			const startTo = start.narrationOutSeconds ?? takeSeconds ?? keptSeconds;

			if (handle === "head") {
				// The edge follows the pointer and the same amount comes off the take, so the
				// words that remain stay put against the picture.
				const offset = Math.min(
					Math.max(0, pointer),
					round1(startTo - startFrom + start.narrationOffsetSeconds - MIN_NARRATION_SECONDS)
				);
				const trimmed = round1(startFrom + (offset - start.narrationOffsetSeconds));

				apply({
					narrationOffsetSeconds: round1(Math.max(0, offset)),
					narrationInSeconds: Math.max(0, trimmed),
				});

				return;
			}

			const end = Math.max(pointer, round1(start.narrationOffsetSeconds + MIN_NARRATION_SECONDS));
			const out = round1(startFrom + (end - start.narrationOffsetSeconds));

			apply({
				narrationOutSeconds: takeSeconds ? Math.min(out, round1(takeSeconds)) : out,
			});
		},
		[apply, clip.inSeconds, keptSeconds, takeSeconds]
	);

	// Tracked on the window rather than the block, so a fast drag that outruns the element
	// keeps following the pointer instead of stopping where it escaped.
	useEffect(() => {
		if (!dragging) {
			return undefined;
		}

		const onMove = (event: PointerEvent) => moveTo(dragging, secondsAt(event.clientX));
		const onUp = () => {
			setDragging(null);
			dragOrigin.current = null;
			onCommit();
		};

		globalThis.addEventListener("pointermove", onMove);
		globalThis.addEventListener("pointerup", onUp);
		globalThis.addEventListener("pointercancel", onUp);

		return () => {
			globalThis.removeEventListener("pointermove", onMove);
			globalThis.removeEventListener("pointerup", onUp);
			globalThis.removeEventListener("pointercancel", onUp);
		};
	}, [dragging, moveTo, onCommit, secondsAt]);

	const beginDrag = (handle: "body" | "head" | "tail", clientX: number) => {
		if (disabled) {
			return;
		}

		dragOrigin.current = { pointerSeconds: secondsAt(clientX), window: windowOf(clip) };
		setDragging(handle);
	};

	const nudgeOffset = (delta: number) => {
		apply({
			narrationOffsetSeconds: Math.min(
				Math.max(0, snap(window_.narrationOffsetSeconds + delta)),
				round1(keptSeconds - CLIP_PRECISION_SECONDS)
			),
		});
		onCommit();
	};

	const isEdited =
		window_.narrationOffsetSeconds > 0 ||
		window_.narrationInSeconds !== null ||
		window_.narrationOutSeconds !== null;

	if (!narrationUrl) {
		return (
			<p className="rounded-card bg-surface px-3 py-2.5 text-xs text-subtle">
				{ts("voiceMissing")}
			</p>
		);
	}

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs font-bold uppercase text-subtle">{ts("voiceHeading")}</span>
				<div className="flex flex-wrap items-center gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={ts("voicePlay")}
						type="button"
						onClick={() => {
							const audio = audioRef.current;

							if (!audio) {
								return;
							}

							audio.currentTime = from;
							void audio.play();
						}}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled || !isEdited}
						size={ButtonSize.XS}
						title={ts("voiceReset")}
						type="button"
						onClick={() => {
							onChange({
								narrationOffsetSeconds: 0,
								narrationInSeconds: null,
								narrationOutSeconds: null,
							});
							onCommit();
						}}
					/>
				</div>
			</div>

			{/* Kept out of the layout: it exists to be measured and to be heard. */}
			<audio
				ref={audioRef}
				className="hidden"
				preload="metadata"
				src={narrationUrl}
				onLoadedMetadata={(event) => {
					const seconds = event.currentTarget.duration;

					if (Number.isFinite(seconds)) {
						setTakeSeconds(round1(seconds));
					}
				}}
				onTimeUpdate={(event) => {
					// Stops where the cut does, so pressing play auditions the kept region
					// rather than the whole take.
					if (event.currentTarget.currentTime >= to) {
						event.currentTarget.pause();
					}
				}}
			/>

			<div ref={trackRef} className="relative h-10 w-full rounded-card bg-surface">
				{/* The region of the picture this voice can sit in. */}
				<div
					className="absolute inset-y-0 rounded-card bg-border-soft/50"
					style={{
						left: `${(clip.inSeconds / durationSeconds) * 100}%`,
						right: `${100 - (clip.outSeconds / durationSeconds) * 100}%`,
					}}
				/>

				<div
					aria-label={ts("voiceBlock")}
					className={classNames(
						"absolute inset-y-1 flex items-center justify-between rounded-card border-2 border-accent bg-accent/25",
						disabled ? "pointer-events-none opacity-50" : "cursor-grab touch-none",
						dragging === "body" && "cursor-grabbing"
					)}
					role="button"
					style={{
						left: `${Math.max(0, leftPercent)}%`,
						width: `${Math.max(2, Math.min(widthPercent, 100 - leftPercent))}%`,
					}}
					tabIndex={disabled ? -1 : 0}
					onKeyDown={(event) => {
						if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
							event.preventDefault();
							nudgeOffset(
								event.key === "ArrowLeft" ? -CLIP_PRECISION_SECONDS : CLIP_PRECISION_SECONDS
							);
						}
					}}
					onPointerDown={(event) => {
						event.preventDefault();
						beginDrag("body", event.clientX);
					}}
				>
					{(["head", "tail"] as const).map((handle) => (
						<span
							key={handle}
							aria-label={ts(handle === "head" ? "voiceCutHead" : "voiceCutTail")}
							className="flex h-full w-3 cursor-ew-resize touch-none items-center justify-center rounded bg-accent text-[10px] font-bold text-ink"
							role="button"
							tabIndex={-1}
							onPointerDown={(event) => {
								// Or the body would take the drag and move the whole block.
								event.stopPropagation();
								event.preventDefault();
								beginDrag(handle, event.clientX);
							}}
						>
							{handle === "head" ? "⟨" : "⟩"}
						</span>
					))}
				</div>
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<span className="text-xs font-bold uppercase text-subtle">{ts("voiceOffset")}</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgeMinus")}
						type="button"
						onClick={() => nudgeOffset(-CLIP_NUDGE_SECONDS)}
					/>
					<span className="whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
						{formatSeconds(window_.narrationOffsetSeconds)}
					</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgePlus")}
						type="button"
						onClick={() => nudgeOffset(CLIP_NUDGE_SECONDS)}
					/>
				</div>

				<span className="rounded-full bg-accent-tint px-2.5 py-1 text-xs font-semibold text-ink">
					{ts("voiceKept", {
						kept: formatSeconds(spokenSeconds),
						total: formatSeconds(takeSeconds ?? spokenSeconds),
					})}
				</span>
			</div>

			<p className="text-xs text-subtle">{ts("voiceHint")}</p>
		</div>
	);
};
